// The demo set for Foyers and Appareils: the design's pilot homes (Bastos,
// Essos, Melen, Mendong), in the shapes ops_homes, ops_home and ops_devices
// return. Deterministic, so the preview looks the same on every load.
import { AGENCY, BRANDS, demoOrders } from './demo';
import type { DeviceKit, Devices, HomeDetail, HomeRow, Kit, Marketer, OpsOrder } from './types';

const DAY = 86_400_000;
const MIN = 60_000;
const iso = (msAgo: number) => new Date(Date.now() - msAgo).toISOString();

function rng(seed: number) {
  let s = seed >>> 0;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 2 ** 32; };
}

type B = keyof typeof BRANDS;
// id, name, quartier, brand, level, kg/day, extra
const SEED: [string, string, string, B, number, number, { silent?: boolean; noKit?: boolean; failed?: boolean; bat?: number; rssi?: number; fw?: string }?][] = [
  ['hh-MG-77D3E1', 'Sylvie Ngo Bell', 'Melen', 'tradex', 6, 0.42],
  ['hh-MG-90AA4C', 'Christine Abena', 'Bastos', 'afrigaz', 9, 0.51],
  ['h8', 'Danielle Ateba', 'Essos', 'total', 13, 0.38],
  ['hh-MG-3B81F0', 'Paul Etoga', 'Essos', 'total', 14, 0.47],
  ['h11', 'Irène Mvogo', 'Bastos', 'afrigaz', 17, 0.35, { bat: 14 }],
  ['h14', 'Yvette Nnanga', 'Melen', 'bocom', 34, 0.33, { silent: true, rssi: -99 }],
  ['h20', 'Hugues Ekotto', 'Mendong', 'tradex', 41, 0.44, { fw: '1.0.2' }],
  ['h21', 'Laure Ndzana', 'Bastos', 'bocom', 52, 0.29],
  ['h22', 'Martin Ayissi', 'Essos', 'afrigaz', 58, 0.41, { rssi: -93 }],
  ['h23', 'Nadège Owono', 'Melen', 'total', 63, 0.37],
  ['hh-MG-5A1D9B', 'Emmanuel Nkoulou', 'Mendong', 'bocom', 99, 0.39],
  ['hh-MG-E44C02', 'Rose Mbarga', 'Essos', 'tradex', 98, 0.45],
  ['hh-MG-1F6A38', 'Didier Owona', 'Melen', 'afrigaz', 98, 0.36],
  ['h24', 'Olivier Tsala', 'Bastos', 'total', 71, 0.32],
  ['hh-MG-8D27B6', 'Laure Ekambi', 'Bastos', 'total', 46, 0.31],
  ['h25', 'Rachel Mengue', 'Mendong', 'tradex', 77, 0.48],
  ['h26', 'Thierry Essama', 'Essos', 'bocom', 84, 0.34],
  ['hh-MG-B9037E', 'Félicité Ondoa', 'Essos', 'tradex', 96, 0.4],
  ['hh-MG-C20E57', 'Brigitte Fouda', 'Bastos', 'afrigaz', 0, 0.4, { noKit: true }],
  ['hh-MG-4F2K9A', 'Gisèle Manga', 'Mendong', 'bocom', 0, 0.4, { noKit: true, failed: true }],
];

interface Built { row: HomeRow; days: { day: string; level: number | null }[] | null; kit: Kit | null }

function build(): Built[] {
  const orders = demoOrders();
  return SEED.map(([id, name, q, b, level, rate, ex = {}], i) => {
    const r = rng(i * 97 + 13);
    const kg = 12.5;
    const pct = (rate / kg) * 100;
    const open = orders.find((o) => o.customer_id === id && !['completed', 'install_completed', 'delivered', 'cancelled_by_customer', 'cancelled_by_agency', 'failed_first_install'].includes(o.status));
    const conn = !ex.noKit;
    // 60 days walked back from today: falling at the home's pace, a refill when it nears empty.
    let days: Built['days'] = null;
    if (conn) {
      const vals: number[] = [];
      let L = level;
      for (let d = 0; d < 60; d++) {
        vals.push(Math.round(Math.max(0, Math.min(100, L))));
        L += pct * (0.75 + r() * 0.5);
        if (L > 99) L = 6 + r() * 12;
      }
      vals.reverse();
      if (level >= 96) vals[58] = 8 + (i % 7);
      days = vals.map((v, k) => ({ day: new Date(Date.now() - (59 - k) * DAY).toISOString().slice(0, 10), level: ex.silent && k >= 58 ? null : v }));
    }
    const lastMin = ex.silent ? 41 * 60 : Math.round(2 + r() * 12);
    const kit: Kit | null = conn ? {
      sensor: { serial: 'US-26-' + String(1040 + i * 7).padStart(6, '0'), battery_pct: ex.bat ?? Math.round(62 + r() * 36) },
      gateway: { serial: 'UC-26-' + String(2210 + i).padStart(6, '0'), battery_pct: Math.round(70 + r() * 30), rssi: ex.rssi ?? -Math.round(58 + r() * 20), fw: ex.fw ?? '1.0.3' },
      measured_at: iso(lastMin * MIN), received_at: iso(lastMin * MIN), online: !ex.silent,
      bound_at: iso(Math.round(20 + r() * 90) * DAY),
      hours24: Array.from({ length: 24 }, () => (ex.silent ? 0 : (ex.rssi ?? -60) < -90 && r() < 0.3 ? 0 : 4)),
    } : null;
    const row: HomeRow = {
      customer_id: id, name, quartier: q, phone: '+2376' + String(70000000 + i * 1234567).slice(0, 8), since: iso(Math.round(30 + r() * 120) * DAY),
      state: conn ? 'active' : 'awaiting_install', connected: conn, size_kg: kg,
      home: conn ? { level, measured_at: kit!.measured_at, received_at: kit!.received_at, online: !ex.silent, gateway: kit!.gateway.serial } : null,
      pace: conn ? { pct_per_day: +pct.toFixed(2), kg_per_day: rate, days_left: Math.floor(level / pct), since: iso(20 * DAY) } : null,
      marketer: BRANDS[b] as Marketer, open_order: open?.id ?? null, failed: !!ex.failed, orders: conn ? Math.round(1 + r() * 5) : 0,
    };
    return { row, days, kit };
  });
}

let cache: Built[] | null = null;
const all = () => (cache ??= build());

export const demoHomes = (): HomeRow[] => all().map((b) => b.row);

export function demoHome(id: string): HomeDetail {
  const b = all().find((x) => x.row.customer_id === id) ?? all()[0];
  const order_list: OpsOrder[] = demoOrders().filter((o) => o.customer_id === b.row.customer_id);
  return { ...b.row, kit: b.kit, days60: b.days, order_list };
}

export function demoDevices(): Devices {
  const kits: DeviceKit[] = all().filter((b) => b.kit).map((b) => ({ ...b.kit!, customer_id: b.row.customer_id, name: b.row.name, quartier: b.row.quartier, level: b.row.home?.level ?? null }));
  const stock: Devices['stock'] = Array.from({ length: 6 }, (_, i) => [
    { kind: 'uconnect' as const, serial: 'UC-26-' + String(2301 + i).padStart(6, '0'), status: (i === 0 ? 'reserved' : 'inventory') as 'reserved' | 'inventory', agency: AGENCY.name, order_id: null },
    { kind: 'ultrasense' as const, serial: 'US-26-' + String(1301 + i).padStart(6, '0'), status: (i === 0 ? 'reserved' : 'inventory') as 'reserved' | 'inventory', agency: AGENCY.name, order_id: null },
  ]).flat();
  return { kits, stock, failed: demoOrders().filter((o) => o.status === 'failed_first_install') };
}
